import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { api } from '../App';
import VideoPlayer from './VideoPlayer';

function fmtSec(s) {
  if (!s) return '';
  const m = Math.floor(s / 60), sec = String(s % 60).padStart(2, '0');
  return `${m}:${sec}`;
}

function useFavorites() {
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hepardon_favs') || '[]'); } catch { return []; }
  });
  const save = (list) => { setFavs(list); localStorage.setItem('hepardon_favs', JSON.stringify(list)); };
  const toggle = (v) => {
    const exists = favs.find(f => f.id === v.id);
    save(exists ? favs.filter(f => f.id !== v.id) : [v, ...favs].slice(0, 50));
  };
  const isFav = (id) => favs.some(f => f.id === id);
  return { favs, toggle, isFav };
}

function ShazamButton({ onResult }) {
  const [status, setStatus] = useState('idle'); // idle | recording | processing | done | error
  const [countdown, setCountdown] = useState(8);
  const [result, setResult] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const countRef = useRef(null);

  const stopRecording = () => {
    clearInterval(countRef.current);
    clearTimeout(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
  };

  const startRecording = async () => {
    if (status === 'recording') { stopRecording(); return; }
    setResult(null);
    setStatus('recording');
    setCountdown(8);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      const chunks = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        setStatus('processing');
        stream.getTracks().forEach(t => t.stop());
        try {
          const blob = new Blob(chunks, { type: mimeType });
          const form = new FormData();
          form.append('audio', blob, 'audio.webm');
          const r = await axios.post(`${api}/shazam`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 25000,
          });
          if (r.data.found) {
            setResult(r.data);
            setStatus('done');
          } else {
            setStatus('error');
          }
        } catch {
          setStatus('error');
        }
      };
      recorder.start(100);
      countRef.current = setInterval(() => setCountdown(c => c - 1), 1000);
      timerRef.current = setTimeout(() => stopRecording(), 8000);
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => () => stopRecording(), []);

  return (
    <div>
      <button
        onClick={startRecording}
        title="Identifier une chanson (Shazam)"
        style={{
          background: status === 'recording'
            ? 'linear-gradient(135deg,#ef4444,#dc2626)'
            : 'linear-gradient(135deg,#f97316,#ea580c)',
          border: 'none', borderRadius: 10, padding: '10px 14px',
          color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
          animation: status === 'recording' ? 'pulse 1s infinite' : 'none',
          position: 'relative',
        }}
      >
        {status === 'recording' ? `⏹ ${countdown}s` : status === 'processing' ? <span className="loader" /> : '🎤'}
      </button>

      {(status === 'done' && result) && (
        <div style={{ marginTop: 8, background: 'var(--bg3)', border: '1px solid var(--purple)', borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>🎵 Chanson identifiée :</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{result.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>👤 {result.artist}{result.album ? ` · ${result.album}` : ''}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn-primary"
              style={{ flex: 1, fontSize: 12, padding: '8px 12px', borderRadius: 8 }}
              onClick={() => { onResult(`${result.artist} ${result.title}`); setStatus('idle'); }}
            >
              🔍 Chercher sur YouTube
            </button>
            <button
              style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text2)', cursor: 'pointer', fontSize: 12 }}
              onClick={() => setStatus('idle')}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div style={{ marginTop: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: 10, fontSize: 12, color: '#ef4444' }}>
          ❌ Chanson non reconnue — réessaie dans un endroit calme.
          <button onClick={() => setStatus('idle')} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </div>
  );
}

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [tab, setTab] = useState('search');
  const [aiLoading, setAiLoading] = useState(false);
  const { favs, toggle, isFav } = useFavorites();

  const search = async (q) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;
    setLoading(true); setPlaying(null); setTab('search');
    try {
      const r = await axios.get(`${api}/search`, { params: { q: searchQuery, limit: 15 } });
      setResults(r.data);
    } catch {}
    setLoading(false);
  };

  const aiSearch = async () => {
    if (!query.trim()) return;
    setAiLoading(true);
    try {
      const r = await axios.post(`${api}/ai/chat`, {
        message: `Cherche moi des vidéos YouTube pour: "${query}". Donne le SEARCH_QUERIES.`,
        history: []
      });
      if (r.data.searchResults?.length) {
        setResults(r.data.searchResults);
        setTab('search');
      } else {
        await search();
      }
    } catch { await search(); }
    setAiLoading(false);
  };

  const handleShazamResult = (q) => {
    setQuery(q);
    search(q);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') search();
  };

  return (
    <div className="page">
      {/* Barre de recherche avec IA + Shazam */}
      <div className="url-box">
        <div className="url-input-row" style={{ marginBottom: 8 }}>
          <input
            className="url-input"
            placeholder="Recherche ou demande à l'IA..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
          />
          <button className="btn-primary" onClick={() => search()} disabled={loading} title="Recherche YouTube">
            {loading ? <span className="loader" /> : '🔍'}
          </button>
          <button
            className="btn-primary"
            onClick={aiSearch}
            disabled={aiLoading || loading}
            title="Recherche IA"
            style={{ background: 'linear-gradient(135deg,#06b6d4,#7c3aed)' }}
          >
            {aiLoading ? <span className="loader" /> : '🤖'}
          </button>
        </div>

        {/* Shazam button */}
        <ShazamButton onResult={handleShazamResult} />

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className={`format-tab ${tab === 'search' ? 'active' : ''}`} style={{ flex: 1 }} onClick={() => { setTab('search'); setPlaying(null); }}>
            🔍 Résultats {results.length > 0 && `(${results.length})`}
          </button>
          <button className={`format-tab ${tab === 'favs' ? 'active' : ''}`} style={{ flex: 1 }} onClick={() => { setTab('favs'); setPlaying(null); }}>
            ⭐ Favoris {favs.length > 0 && `(${favs.length})`}
          </button>
        </div>
      </div>

      {/* Lecteur intégré */}
      {playing && (
        <VideoPlayer
          key={playing.id}
          videoId={playing.id}
          title={playing.title}
          uploader={playing.uploader}
          onClose={() => setPlaying(null)}
          onFav={() => toggle(playing)}
          isFav={isFav(playing.id)}
          url={playing.url}
        />
      )}

      {/* Liste résultats ou favoris */}
      <div className="search-grid">
        {(tab === 'search' ? results : favs).map(v => (
          <VideoCard
            key={v.id}
            v={v}
            playing={playing}
            onPlay={() => setPlaying(playing?.id === v.id ? null : v)}
            onFav={() => toggle(v)}
            isFav={isFav(v.id)}
          />
        ))}
      </div>

      {tab === 'favs' && favs.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--text2)', marginTop: 60 }}>
          <div style={{ fontSize: 48 }}>⭐</div>
          <div style={{ marginTop: 12, fontSize: 15 }}>Aucun favori — appuie sur ⭐ pour en ajouter</div>
        </div>
      )}

      {tab === 'search' && !loading && results.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--text2)', marginTop: 60 }}>
          <div style={{ fontSize: 48 }}>🎵</div>
          <div style={{ marginTop: 12, fontSize: 15 }}>Recherche 🔍, demande à l'IA 🤖, ou identifie avec 🎤</div>
        </div>
      )}
    </div>
  );
}

function VideoCard({ v, playing, onPlay, onFav, isFav }) {
  return (
    <div className="search-card" style={playing?.id === v.id ? { borderColor: 'var(--purple)' } : {}}>
      <div className="search-thumb" onClick={onPlay} style={{ cursor: 'pointer', position: 'relative' }}>
        <img src={v.thumbnail} alt={v.title} loading="lazy" style={{ height: 80 }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
          <span style={{ fontSize: 22 }}>{playing?.id === v.id ? '⏹' : '▶'}</span>
        </div>
        <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.8)', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>
          {v.duration_string || fmtSec(v.duration)}
        </div>
      </div>
      <div className="search-card-info">
        <div className="search-card-title">{v.title}</div>
        <div className="search-card-meta">👤 {v.uploader}</div>
        <div className="search-card-actions">
          <button className="btn-xs primary" onClick={onPlay}>
            {playing?.id === v.id ? '⏹' : '▶'}
          </button>
          <button className="btn-xs" onClick={onFav} style={isFav ? { borderColor: 'gold', color: 'gold' } : {}}>
            {isFav ? '⭐' : '☆'}
          </button>
          <button className="btn-xs" onClick={() => navigator.clipboard?.writeText(v.url).catch(() => {})}>
            📋
          </button>
        </div>
      </div>
    </div>
  );
}
